import CollectionDataGrid from '@/components/CollectionDataGrid'
import { pbAddr, pbUpsert, useCollection, usePaginatedCollection } from '@/pb'
import CloseIcon from '@mui/icons-material/Close'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import WavesurferPlayer from '@wavesurfer/react'
import { useEffect, useMemo, useState } from 'react'
import Hover from 'wavesurfer.js/dist/plugins/hover.esm.js'

const getMusicFileUrl = (record) => {
  if (!record?.id || !record?.file) return null
  return `${pbAddr}/api/files/music/${record.id}/${record.file}`
}

const MetricCard = ({ label, value }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        minHeight: 88,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.5,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Paper>
  )
}

const Editor = ({ row, onClose }) => {
  const theme = useTheme()

  const creating = !row.id

  const [musicListRaw, musicLoaded] = useCollection('music')
  const musicList = Array.isArray(musicListRaw)
    ? musicListRaw
    : Object.values(musicListRaw || {})

  const [selectedMusic, setSelectedMusic] = useState(null)
  const [musicIdInput, setMusicIdInput] = useState(row?.musicId || '')
  const [nameInput, setNameInput] = useState(row?.name || '')
  const [descriptionInput, setDescriptionInput] = useState(row?.description || '')

  const [wavesurfer, setWavesurfer] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [markers, setMarkers] = useState([])
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null)

  useEffect(() => {
    if (row?.musicId && musicLoaded && musicList.length > 0) {
      const music = musicList.find((m) => m.id === row.musicId)
      if (music) {
        setSelectedMusic(music)
        setMusicIdInput(music.id)

        const timestamps = row.punchTargets || []
        const initialMarkers = timestamps.map((time, idx) => ({
          id: idx + Date.now(),
          relativeX: 0,
          time,
        }))
        setMarkers(initialMarkers)
      }
    }
  }, [musicLoaded])

  useEffect(() => {
    if (musicIdInput && musicList.length > 0 && creating) {
      const music = musicList.find((m) => m.id === musicIdInput)
      setSelectedMusic(music)
      setMarkers([])
    }
  }, [musicIdInput])

  const wavesurferPlugins = useMemo(
    () => [
      Hover.create({
        lineColor: '#000',
        lineWidth: 2,
        labelBackground: '#000',
        labelColor: '#fff',
        labelSize: '14px',
        labelPreferLeft: false,
      }),
    ],
    []
  )

  const handleReady = (ws) => {
    setWavesurfer(ws)
    setIsReady(true)

    if (markers.length > 0) {
      const duration = ws.getDuration()
      setMarkers((prev) =>
        prev.map((m) => ({
          ...m,
          relativeX: m.time / duration,
        }))
      )
    }
  }

  const handlePlayStop = () => {
    if (!wavesurfer) return
    if (isPlaying) {
      wavesurfer.stop()
      return
    }
    wavesurfer.play()
  }

  const addMarker = (relativeX) => {
    if (!wavesurfer) return
    const duration = wavesurfer.getDuration()
    const time = duration * relativeX
    setMarkers((prev) => [
      ...prev,
      {
        id: Date.now(),
        relativeX,
        time,
      },
    ])
  }

  const addMarkerAtCurrent = () => {
    if (!wavesurfer) return
    const duration = wavesurfer.getDuration()
    const time = wavesurfer.getCurrentTime()
    const relativeX = time / duration
    setMarkers((prev) => [
      ...prev,
      {
        id: Date.now(),
        relativeX,
        time,
      },
    ])
  }

  const removeMarkerById = (id) => {
    setMarkers((prev) => prev.filter((m) => m.id !== id))
  }

  const handleSave = async () => {
    if (!selectedMusic?.id) return

    const punchTargets = markers.map((m) => m.time)

    await pbUpsert('music_workout_templates', {
      id: row?.id,
      name: nameInput,
      description: descriptionInput,
      musicId: selectedMusic.id,
      punchTargets,
    })
    onClose()
  }

  const handleClose = () => {
    wavesurfer?.pause()
    wavesurfer?.destroy()
    onClose()
  }

  const fileUrl = selectedMusic ? getMusicFileUrl(selectedMusic) : null

  useEffect(() => {
    const onKey = event => {
      console.log(event, event.keyCode)
      if (event.keyCode === 191 || event.keyCode === 190) { // dots
        addMarkerAtCurrent()
        event.preventDefault()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [wavesurfer])

  return (
    <Dialog open onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {!creating ? 'Редактировать тренировку' : 'Новая тренировка под трек'}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="editorForm">
        <Box>
          <TextField
            label="Название тренировки"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            fullWidth
          >
          </TextField>
        </Box>

        <Box>
          <TextField
            label="Описание тренировки"
            multiline
            minRows={3}
            maxRows={10}
            value={descriptionInput}
            onChange={(e) => setDescriptionInput(e.target.value)}
            fullWidth
          >
          </TextField>
        </Box>

        <Box>
          <TextField
            select
            label="Выберите трек"
            value={musicIdInput}
            onChange={(e) => setMusicIdInput(e.target.value)}
            fullWidth
            disabled={!creating || !musicLoaded}
            helperText={!creating ? 'Трек уже выбран' : ''}
          >
            <MenuItem value="">
              <em>Не выбрано</em>
            </MenuItem>
            {musicList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.title} – {item.author}
              </MenuItem>
            ))}
          </TextField>
        </Box>



        {selectedMusic && fileUrl && (
          <Box>
            <Box sx={{ position: 'relative', height: 100, mb: 2 }}>
              {!isReady && <LinearProgress variant="indeterminate" />}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 100,
                  pointerEvents: 'none',
                }}
              >
                {markers.map((marker) => {
                  const isHovered = hoveredMarkerId === marker.id
                  return (
                    <Box
                      key={marker.id}
                      onMouseEnter={() => setHoveredMarkerId(marker.id)}
                      onMouseLeave={() => setHoveredMarkerId(null)}
                      onClick={(e) => {
                        e.stopPropagation()
                        removeMarkerById(marker.id)
                      }}
                      sx={{
                        position: 'absolute',
                        left: `${marker.relativeX * 100}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: '#000',
                        border: '3px solid #fff',
                        outline: '1px solid black',
                        boxShadow: 1,
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.15s ease',
                        '&:hover': {
                          transform: 'translate(-50%, -50%) scale(1.2)',
                        },
                      }}
                    >
                      <CloseRoundedIcon
                        sx={{
                          fontSize: 14,
                          color: '#000',
                          background: '#fff',
                          borderRadius: '50%',
                          opacity: isHovered ? 1 : 0,
                          pointerEvents: 'none',
                        }}
                      />
                    </Box>
                  )
                })}
              </Box>
              <WavesurferPlayer
                interact
                height={100}
                waveColor={theme.palette.primary.light}
                progressColor={theme.palette.primary.main}
                url={fileUrl}
                onReady={handleReady}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onFinish={() => setIsPlaying(false)}
                onClick={(e, relativeX) => addMarker(relativeX)}
                plugins={wavesurferPlugins}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handlePlayStop}
                disabled={!isReady}
              >{isPlaying ? <StopIcon /> : <PlayArrowIcon />}
              </Button>
              <Typography variant="body2" sx={{ ml: 'auto' }}>
                {markers.length} меток
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Отмена</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedMusic || markers.length === 0}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const TrainingDialog = ({ row, onClose }) => {
  const theme = useTheme()
  const [wavesurfer, setWavesurfer] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [markers, setMarkers] = useState([])

  const [punches, setPunches] = useState(row?.punches || [])
  const [stats, setStats] = useState({
    avgDelay: 0,
    efficiency: 0,
    hitTargets: 0,
    totalTargets: 0,
  })
  const workout = row

  const music = row?.expand?.musicId || null
  const fileUrl = music ? getMusicFileUrl(music) : null

  const wavesurferPlugins = useMemo(
    () => [
      Hover.create({
        lineColor: '#000',
        lineWidth: 2,
        labelBackground: '#000',
        labelColor: '#fff',
        labelSize: '14px',
        labelPreferLeft: false,
      }),
    ],
    []
  )

  useEffect(() => {
    if (!workout?.id) return

    const clearPunches = async () => {
      const existing = await pb.collection('music_workouts').getList(1, 1, {
        filter: `templateId = "${workout.id}"`,
      })

      const record = existing.items[0]
      if (!record) return

      await pb.collection('music_workouts').update(record.id, {
        punches: [],
      })
    }

    clearPunches()
  }, [workout?.id])

  function mapPunchesToTargets(punches, targets) {
    if (!punches || !targets || targets.length === 0) return {};

    const targetMap = {};
    targets.forEach(t => { targetMap[t] = null; });

    for (const punchTime of punches) {
      let closestTarget = null;
      let minDiff = Infinity;

      for (const targetTime of targets) {
        const diff = Math.abs(punchTime - targetTime);
        if (diff < minDiff) {
          minDiff = diff;
          closestTarget = targetTime;
        }
      }

      if (closestTarget !== null) {
        const existing = targetMap[closestTarget];
        if (existing === null || minDiff < Math.abs(existing - closestTarget)) {
          targetMap[closestTarget] = punchTime;
        }
      }
    }

    return targetMap;
  }

  function computeStats(targetMap, targets, punches) {
    // console.log("computeStats:: ", targetMap, targets, punches)
    const totalTargets = targets.length;
    const occupiedTimestamps = Object.values(targetMap).filter((v) => v !== null).length;
    const totalHits = punches.length;

    let totalDelay = 0;
    let count = 0;

    for (const targetTime of targets) {
      const punchTime = targetMap[targetTime];
      if (punchTime !== null) {
        totalDelay += Math.abs(punchTime - targetTime);
        count++;
      }
    }

    const avgDelay = count > 0 ? totalDelay / count : 0;
    const efficiency = totalHits > 0 ? occupiedTimestamps / totalHits : 0;

    return {
      avgDelay,
      efficiency: isNaN(efficiency) ? 0 : efficiency,
      hitTargets: occupiedTimestamps,
      totalTargets,
    };
  }

  useEffect(() => {
    if (!workout?.id) return

    let isMounted = true
    let unsubscribeId = null

    const subscribeToPunches = async () => {
      const existing = await pb.collection('music_workouts').getList(1, 1, {
        filter: `templateId = "${workout.id}"`,
      })

      const record = existing.items[0] || null
      if (!record) {
        if (isMounted) setPunches([])
        return
      }

      unsubscribeId = record.id
      if (isMounted) setPunches(record.punches || [])

      await pb.collection('music_workouts').subscribe(record.id, (e) => {
        if (!isMounted) return
        if (e.action === 'update') {
          const updatedPunches = e.record?.punches || []
          setPunches(updatedPunches)
        }
      })
    }

    subscribeToPunches()

    return () => {
      isMounted = false
      if (unsubscribeId) {
        pb.collection('music_workouts').unsubscribe(unsubscribeId)
      }
    }
  }, [workout?.id])

  useEffect(() => {
    const targets = Array.isArray(workout?.punchTargets) ? workout.punchTargets : []

    if (targets.length === 0) {
      setStats({ avgDelay: 0, efficiency: 0, hitTargets: 0, totalTargets: 0 })
      return
    }

    const targetMap = mapPunchesToTargets(punches, targets)
    setStats(computeStats(targetMap, targets, punches))
  }, [punches, workout?.punchTargets])


  const handlePunch = async () => {
    if (!workout?.id || !wavesurfer) return

    const timestamp = wavesurfer.getCurrentTime()

    await fetch('/api/musicworkouts/punch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workoutId: workout.id,
        timestamp,
      }),
    })
  }

  useEffect(() => {
    const punchTargets = Array.isArray(row?.punchTargets) ? row.punchTargets : []
    setMarkers(
      punchTargets.map((time, idx) => ({
        id: `${row?.id || 'training'}-${idx}`,
        time,
        relativeX: 0,
      }))
    )
  }, [row])

  const handleReady = (ws) => {
    setWavesurfer(ws)
    setIsReady(true)

    const duration = ws.getDuration()
    if (duration > 0) {
      setMarkers((prev) =>
        prev.map((m) => ({
          ...m,
          relativeX: m.time / duration,
        }))
      )
    }
  }

  const handlePlayStop = () => {
    if (!wavesurfer) return
    if (isPlaying) {
      wavesurfer.stop()
      return
    }
    wavesurfer.play()
  }


  const handleClose = () => {
    wavesurfer?.pause()
    wavesurfer?.destroy()
    onClose()
  }

  const averageDelay = `${stats.avgDelay.toFixed(2)}c`
  const efficiency = `${(stats.efficiency).toFixed(0)}%`

  return (
    <Dialog open onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Тренировка
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ position: 'relative', height: 120, mb: 2 }}>
          {!isReady && <LinearProgress variant="indeterminate" />}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 100,
              pointerEvents: 'none',
            }}
          >
            {markers.map((marker) => (
              <Box
                key={marker.id}
                sx={{
                  position: 'absolute',
                  left: `${marker.relativeX * 100}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: '#000',
                  border: '3px solid #fff',
                  outline: '1px solid black',
                  boxShadow: 1,
                  pointerEvents: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'transform 0.15s ease',
                }}
              />
            ))}
          </Box>
          {fileUrl && (
            <WavesurferPlayer
              interact={false}
              height={120}
              waveColor={theme.palette.primary.light}
              progressColor={theme.palette.primary.main}
              url={fileUrl}
              onReady={handleReady}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onFinish={() => setIsPlaying(false)}
              plugins={wavesurferPlugins}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
          <Button
            variant="contained"
            onClick={handlePlayStop}
            disabled={!isReady}
          >{isPlaying ? <StopIcon /> : <PlayArrowIcon />}
          </Button>
          <Button variant="outlined" onClick={handlePunch} disabled={!isReady}>
            Удар
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <MetricCard label="Среднее отстование" value={averageDelay} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MetricCard label="Эффективность" value={efficiency} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  )
}

const getColumns = (onStartTraining) => [
  { field: 'name', headerName: 'Название', width: 250 },
  { field: 'description', headerName: 'Описание', width: 400 },
  {
    field: 'expand.musicId',
    headerName: 'Название трека',
    width: 250,
    renderCell: ({ row }) => {
      const music = row?.expand?.musicId
      return <span>{music?.title || '—'}</span>
    },
  },
  {
    field: 'author',
    headerName: 'Автор трека',
    width: 200,
    renderCell: ({ row }) => {
      const music = row?.expand?.musicId
      return <span>{music?.author || '—'}</span>
    },
  },
  // {
  //   field: 'punchTargets',
  //   headerName: 'Метки ударов',
  //   width: 50,
  //   renderCell: ({ value }) => <span>{Array.isArray(value) ? `${value.length}` : '—'}</span>,
  // },
  // {
  //   field: 'actions',
  //   headerName: '',
  //   width: 250,
  //   sortable: false,
  //   filterable: false,
  //   renderCell: ({ row }) => (
  //     <Button size="small" variant="outlined" onClick={() => onStartTraining(row)}>
  //       Начать тренировку
  //     </Button>
  //   ),
  // },
]

export default function MusicWorkouts({ title }) {
  const [trainingRow, setTrainingRow] = useState(null)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })

  const {
    records: workouts,
    totalItems,
    loaded,
  } = usePaginatedCollection('music_workout_templates', {
    page: paginationModel.page + 1,
    perPage: paginationModel.pageSize,
    sort: '-created',
    expand: 'musicId'
  })

  const columns = useMemo(() => getColumns((row) => setTrainingRow(row)), [])

  return (
    <>
      <CollectionDataGrid
        collectionName={title || 'Тренировки под треки'}
        collection={workouts}
        columns={columns}
        loading={!loaded}
        editor={Editor}
        serverPagination={true}
        rowCount={totalItems}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />

      {trainingRow && (
        <TrainingDialog row={trainingRow} onClose={() => setTrainingRow(null)} />
      )}
    </>
  )
}